import { useLayoutEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { Palette } from '$setup/material-theme'
import useDevicePixelRatio from '$hooks/use-device-pixel-ratio'
import { drawAnimatedCircle } from './avatar-story-circle-animation'
import { useStateRef } from '$hooks/use-state-ref'
import { useLastCallback } from '$hooks/use-last-callback'
import { AvatarSize } from '../types'
import { AVATAR_SIZES, AVATAR_STROKE_WIDTHS } from '../conf'
import { CallType } from '@weblite/ts-action/media-proxy'

export const REM = parseInt(
  getComputedStyle(document.documentElement).fontSize,
  10,
)

interface OwnProps {
  // eslint-disable-next-line react/no-unused-prop-types
  className?: string
  // size: AvatarSize
  size: AvatarSize
  withExtraGap?: boolean
}

interface StateProps {
  seenStoryCount: number
  storyCount: number
  uploading?: boolean
  ongoingCallType?: CallType
}

type CircleColor = 'story-blue' | 'story-gray' | CallType

const STORY_GRADIENT = ['#5458F7', '#1ABAFD']
const MEETING_GRADIENT_H = ['#1BBBFE', '#01A0E2']
const TRIBUNE_GRADIENT_H = ['#7C63F8', '#5454F7']
const LIVE_GRADIENT_H = ['#FF61A3', '#FF1A79']

const GRAY = [Palette.neutral400, Palette.neutral400]
const GAP_PERCENT = 2
const SEGMENTS_MAX = 45 // More than this breaks rendering in Safari and Chrome

const GAP_PERCENT_EXTRA = 10
const EXTRA_GAP_ANGLE = Math.PI / 4
const EXTRA_GAP_SIZE = (GAP_PERCENT_EXTRA / 100) * (2 * Math.PI)
const EXTRA_GAP_START = EXTRA_GAP_ANGLE - EXTRA_GAP_SIZE / 2
const EXTRA_GAP_END = EXTRA_GAP_ANGLE + EXTRA_GAP_SIZE / 2

const ANIMATION_INITIAL_PROGRESS = 0.5
const ANIMATION_SPEED = 0.0003
const EPSILON = 0.03

export function AvatarStoryCircle({
  size,
  className,
  withExtraGap,
  seenStoryCount,
  storyCount,
  uploading,
  ongoingCallType,
}: OwnProps & StateProps) {
  const strokeWidth = AVATAR_STROKE_WIDTHS[size]

  const [animating, setAnimating] = useState(false)
  const animatingRef = useStateRef(animating)
  const prevAnimationTimeRef = useRef(new Date().getMilliseconds())
  const uploadingRef = useStateRef(uploading)
  const animationProgressRef = useRef(ANIMATION_INITIAL_PROGRESS)
  const values = {
    read: seenStoryCount,
    total: storyCount,
  }
  const isReadRef = useRef(values.total <= values.read)
  isReadRef.current = values.total <= values.read

  const ref = useRef<HTMLCanvasElement>(null)

  const dpr = useDevicePixelRatio()
  const dprRef = useStateRef(dpr)

  const animateStoryCircle = useLastCallback(
    (
      canvas: HTMLCanvasElement | undefined,
      size: number,
      strokeWidth: number,
      readColor: string,
    ) => {
      if (!canvas) return
      if (!animatingRef.current) {
        animationProgressRef.current = ANIMATION_INITIAL_PROGRESS
        return
      }
      const innerDpr = dprRef.current
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      const canvasSize = size * innerDpr
      canvas.width = canvasSize
      canvas.height = canvasSize
      const lineWidth = modifyStroke(strokeWidth, canvasSize, innerDpr)
      const radius = (canvasSize - lineWidth) / 2
      const centerCoordinate = canvasSize / 2
      const color: CircleColor =
        ongoingCallType || (isReadRef.current ? 'story-gray' : 'story-blue')
      const gradient = createGradient(ctx, canvasSize, color)
      const strokeStyle = isReadRef.current ? readColor : gradient
      ctx.lineCap = 'round'

      const circle = {
        radius,
        center: { x: centerCoordinate, y: centerCoordinate },
      }
      ctx.clearRect(0, 0, canvasSize, canvasSize)

      const time = new Date().getMilliseconds()
      const timeDelta = time - prevAnimationTimeRef.current
      let progress = ANIMATION_SPEED * timeDelta
      if (progress < 0) progress = 0
      progress = Number(progress.toFixed(6))
      prevAnimationTimeRef.current = time
      animationProgressRef.current += progress

      if (!uploadingRef.current) {
        const delta = Math.abs(
          animationProgressRef.current - ANIMATION_INITIAL_PROGRESS,
        )
        if (delta <= EPSILON) {
          setAnimating(false)
        }
      }
      if (animationProgressRef.current > 1) {
        animationProgressRef.current = 0
      }

      drawAnimatedCircle(
        ctx,
        circle,
        animationProgressRef.current,
        strokeStyle,
        lineWidth,
      )

      requestAnimationFrame(() =>
        animateStoryCircle(canvas, size, strokeWidth, readColor),
      )
    },
  )

  const color: CircleColor =
    ongoingCallType || (isReadRef.current ? 'story-gray' : 'story-blue')

  useLayoutEffect(() => {
    const canvas = ref.current
    if (!canvas) {
      return
    }

    if (uploading && !animating) {
      prevAnimationTimeRef.current = new Date().getMilliseconds()
      setAnimating(true)
      animatingRef.current = true
      animateStoryCircle(canvas, AVATAR_SIZES[size], strokeWidth, GRAY[0])
    }
    if (!animating)
      drawGradientCircle({
        canvas,
        size: AVATAR_SIZES[size] * dpr,
        segmentsCount: values.total,
        color,
        readSegmentsCount: values.read,
        withExtraGap,
        readSegmentColor: GRAY[0],
        dpr,
        strokeWidth,
      })
  }, [
    size,
    values.read,
    values.total,
    withExtraGap,
    dpr,
    animateStoryCircle,
    uploading,
    animating,
  ])

  const maxSize = AVATAR_SIZES[size]

  return (
    <canvas
      ref={ref}
      className={clsx('avatar-base__story-circle', size, className)}
      style={{ maxWidth: `${maxSize}px`, maxHeight: `${maxSize}px` }}
    />
  )
}

export function drawGradientCircle({
  canvas,
  size,
  color,
  segmentsCount,
  readSegmentsCount = 0,
  withExtraGap = false,
  readSegmentColor,
  dpr,
  strokeWidth,
}: {
  canvas: HTMLCanvasElement
  size: number
  color: CircleColor
  segmentsCount: number
  readSegmentsCount?: number
  withExtraGap?: boolean
  readSegmentColor: string
  dpr: number
  strokeWidth: number
}) {
  if (segmentsCount > SEGMENTS_MAX) {
    readSegmentsCount = Math.round(
      readSegmentsCount * (SEGMENTS_MAX / segmentsCount),
    )

    segmentsCount = SEGMENTS_MAX
  }

  const ctx = canvas.getContext('2d')
  if (!ctx) return

  canvas.width = size
  canvas.height = size
  const lineWidth = modifyStroke(strokeWidth, size, dpr)
  const centerCoordinate = size / 2
  const radius = (size - lineWidth) / 2
  const segmentAngle = (2 * Math.PI) / segmentsCount
  const gapSize = (GAP_PERCENT / 100) * (2 * Math.PI)
  const gradient = createGradient(ctx, size, color)

  ctx.lineCap = 'round'
  ctx.clearRect(0, 0, size, size)

  Array.from({ length: segmentsCount }).forEach((_, i) => {
    const isRead = i < readSegmentsCount
    let startAngle = i * segmentAngle - Math.PI / 2 + gapSize / 2
    let endAngle = startAngle + segmentAngle - (segmentsCount > 1 ? gapSize : 0)

    ctx.strokeStyle = isRead ? readSegmentColor : gradient
    ctx.lineWidth = lineWidth

    if (withExtraGap) {
      if (startAngle >= EXTRA_GAP_START && endAngle <= EXTRA_GAP_END) {
        // Segment is inside extra gap
        return
      } else if (startAngle < EXTRA_GAP_START && endAngle > EXTRA_GAP_END) {
        // Extra gap is inside segment
        ctx.beginPath()
        ctx.arc(
          centerCoordinate,
          centerCoordinate,
          radius,
          EXTRA_GAP_END,
          endAngle,
        )
        ctx.stroke()

        endAngle = EXTRA_GAP_START
      } else if (startAngle < EXTRA_GAP_START && endAngle > EXTRA_GAP_START) {
        // Segment ends in extra gap
        endAngle = EXTRA_GAP_START
      } else if (startAngle < EXTRA_GAP_END && endAngle > EXTRA_GAP_END) {
        // Segment starts in extra gap
        startAngle = EXTRA_GAP_END
      }
    }

    ctx.beginPath()
    ctx.arc(centerCoordinate, centerCoordinate, radius, startAngle, endAngle)
    ctx.stroke()
  })
}

function createGradient(
  ctx: CanvasRenderingContext2D,
  size: number,
  color: CircleColor,
) {
  const gradient = ctx.createLinearGradient(
    0,
    0,
    Math.ceil(size * Math.cos(Math.PI / 2)),
    Math.ceil(size * Math.sin(Math.PI / 2)),
  )

  const colorStops =
    color === 'story-gray'
      ? GRAY
      : color === 'tribune'
        ? TRIBUNE_GRADIENT_H
        : color === 'live' || color === 'classroom'
          ? LIVE_GRADIENT_H
          : color === 'group_call'
            ? MEETING_GRADIENT_H
            : STORY_GRADIENT

  colorStops.forEach((colorStop, index) => {
    gradient.addColorStop(index / (colorStops.length - 1), colorStop)
  })

  return gradient
}

function modifyStroke(strokeWidth: number, size: number, dpr: number) {
  const strokeModifier =
    Math.max(
      Math.max(size - AVATAR_SIZES[AvatarSize.LG] * dpr, 0) / dpr / REM / 1.5,
      1,
    ) * dpr

  return strokeModifier * strokeWidth
}
